import { ItemIDs, sendRequestRewardItems } from '../Config/Items';
import Core from '../Core/Core';
import getLogger, { ILogger } from '../Core/Logger';
import { SocialActionTypes } from '../DeltaTracking/SocialParamBuilder';
import { DDNAEvents, ParamGroups } from '../DeltaTracking/Tracker';
import EntryTracking, { EntryPoints } from '../Managers/Tracking/EntryTracking';
import HelperTracking, { HelperTrackStages } from '../Managers/Tracking/HelperTracking';
import SocialTracking from '../Managers/Tracking/SocialTracking';
import { Actions } from '../State/Actions/Actions';
import { getRandomReward, IRewardItem } from '../State/reducers/score';
import { isMessageRecieved } from '../State/reducers/social';
import { InviteBotImage } from '../UI/PopUps/Social/InviteBotImage';
import { ShareHelpers } from '../Utils/ShareHelpers';
import { getRandomId } from '../Utils/Utils';
import { PayloadType } from './PayloadType';
import { IEntryPointData, IPayloadData, IPayloadGiftData } from './SDK';
import { IFBFriend, IFBSharePayload } from './SDKfb';
import { ILocalSharePayload } from './SDKlocal';
import { IXPromoConfig, SDKsg } from './SDKsg';
import ContextPlayer = FBInstant.ContextPlayer;

export type ISharePayload = IFBSharePayload | ILocalSharePayload;

export enum GiftType {
    COINS = 'coin',
    LIFE = 'life',
    FIRECRACKER = 'Firecracker',
    MAGNET = 'Magnet',
    HINT = 'Hint',
    SHUFFLE = 'Shuffle',
    FLASHLIGHT = 'Flashlight',
}

export interface IMediaParams {
    gif?: IMediaContent;
    video?: IMediaContent;
}

interface IMediaContent {
    url: string;
}

export enum ShareableMoments {
    NEW_MAP = 'new map',
    JACKPOT = 'jackpot',
}

const sdkLogger: ILogger = getLogger('[SDK-MESSAGES]:');

export interface ISDKmessages {
    sendInvite: () => Promise<any>;
    getEntryPointData: () => IEntryPointData;
    setEntryPointData: (p: IEntryPointData) => Promise<any>;
    processPayloads: () => void;
    canCreateShortcut: () => Promise<any>;
    createShortcut: () => Promise<any>;
    processReceivedInvites: () => Promise<any>;
    processConfirmedInvites: () => Promise<any>;
    askFriend: (friend: IFBFriend, item: IRewardItem) => Promise<boolean>;
    getRandomFriend: () => Promise<IFBFriend>;
    getContextPlayers: () => Promise<ContextPlayer[]>;
    sendCustomMessage: (
        textKey: string,
        image: string,
        friendId?: string,
        payload?: IPayloadData,
        cta?: string
    ) => Promise<any>;
    getFriends: () => Promise<any>;
    playWithFriends: (shareText: string) => Promise<any>;
    sendCoins: (friendId: string, coins: number) => Promise<boolean>;
    sendGift: (friendId: string, shouldCreateContext?: boolean) => Promise<boolean>;
    requestGift: (friendId: string) => Promise<boolean>;
    share: (shareMoment: string, payload?: string) => Promise<any>;
    getXPromo: () => Promise<IXPromoConfig[]>;
    canSwitchGame: () => boolean;
    switchGame: (appId: string, gameName: string, placement: string) => Promise<void>;
}

export class SDKmessages implements ISDKmessages {
    public platform: any;
    public backend: SDKsg;
    public confirmedInvites: any[] = [];
    public receivedInvites: any[] = [];
    public currentPayloads: any[] = [];
    public xPromoPayloads: any[] = [];
    private entryPointData: IEntryPointData;

    constructor(platform: any, backend: SDKsg) {
        this.platform = platform;
        this.backend = backend;
    }

    public async setEntryPointData(entryPointData: any) {
        const { entryPoint, gamePayload, payload: entryPointPayload } = entryPointData;
        const newPayload = gamePayload || entryPointPayload || entryPointData;
        const convertedData: IEntryPointData = {
            entryPoint,
            payload: newPayload,
        };

        this.entryPointData = convertedData;
        sdkLogger.debug(['set entry point data: ', this.entryPointData]);

        if (this.entryPointData.payload) {
            const { payload } = this.entryPointData;

            if (payload.rewardedUserId && payload.rewardedUserId !== Core.sdk.user.userId) {
                return;
            }

            if (payload.originGameId) {
                this.xPromoPayloads.push(payload);
            }

            sdkLogger.debug(['entry point payload', payload]);

            // invite payloads
            if (payload.type === PayloadType.CONFIRMED_INVITE) {
                // payload when friend joins from your invite

                this.confirmedInvites.push(payload);
            } else if (
                payload.type === PayloadType.MAP_COMPLETE_SHARE ||
                payload.type === PayloadType.JACKPOT_SHARE
            ) {
                // payload when joining from a shared post
                this.currentPayloads.push(payload);
            } else if (payload.type === PayloadType.RECEIVED_INVITE) {
                // payload from joining via invite
                this.receivedInvites.push(payload);
                EntryTracking.track(EntryPoints.INVITE);
                sdkLogger.debug(['pushed payload: ', payload]);
            } else if (payload.type === PayloadType.SEND_GIFT) {
                this.currentPayloads.push(payload);
                EntryTracking.track(EntryPoints.SEND_GIFT);
                sdkLogger.debug(['pushed payload: ', payload]);
            } else if (payload.type === PayloadType.HELPER_GIFT) {
                this.currentPayloads.push(payload);
                HelperTracking.track(payload.helperItem, HelperTrackStages.ENTRY);
                sdkLogger.debug(['pushed payload: ', payload]);
            } else if (payload.type === PayloadType.REQUEST_GIFT) {
                this.currentPayloads.push(payload);
                EntryTracking.track(EntryPoints.REQUEST_GIFT);
                sdkLogger.debug(['pushed payload: ', payload]);
            } else if (payload.type === PayloadType.SEND_COINS) {
                this.currentPayloads.push(payload);
                EntryTracking.track(EntryPoints.COINS);
                sdkLogger.debug(['pushed payload: ', payload]);
            } else {
                // bot message payloads
                if (payload.gift) {
                    this.currentPayloads.push(payload);
                }
            }
        }

        return;
    }

    public getEntryPointData() {
        return this.entryPointData;
    }

    public async canCreateShortcut() {
        return this.platform.canCreateShortcut();
    }

    public async createShortcut() {
        return this.platform.createShortcut();
    }

    public canSwitchGame() {
        return this.platform.canSwitchGame();
    }

    public async switchGame(appId: string, gameName: string, placement: string) {
        return this.platform.switchGame(appId, gameName, placement);
    }

    public async getXPromo(): Promise<IXPromoConfig[]> {
        const { XPromo = [] } = this.backend ? await this.backend.getConfig() : {};
        return XPromo;
    }

    /**
     * process payloads from standard bot messages
     */
    public async processPayloads() {
        sdkLogger.debug(['current payloads' + this.currentPayloads]);

        const userId: string = Core.sdk.user.userId;

        // send social events here

        for (const payload of this.currentPayloads) {
            sdkLogger.debug(['processPayloads:', payload]);

            switch (payload.type) {
                case PayloadType.SEND_GIFT:
                    // A friend sent a gift to the player.
                    if (payload.rewardedUserId && payload.rewardedUserId !== userId) {
                        // It's a reward (gift) to another player, get out of here!
                        return;
                    }
                    break;
                case PayloadType.REQUEST_GIFT:
                    // A friend requested a gift to the player.
                    if (payload.userId && payload.userId !== userId) {
                        // Be a nice friend and send him back a gift.
                        // It's not necessary to create a context 'cause it must already be in one.
                        await this.sendGift(payload.userId, false);
                    }
                    return;
                case PayloadType.MAP_COMPLETE_SHARE:
                case PayloadType.JACKPOT_SHARE:
                    const isNew =
                        Object.keys(Core.store.getState().StatePlayer.mapLevels).length === 0 ||
                        Core.store.getState().StatePlayer.totalStars === 0;
                    const p = payload.mapId || '';
                    SocialTracking.joinedFromShare(payload.type, p, isNew);
                    break;
            }

            // send resource events here
            // handle receive gift
            if (payload.gift) {
                if (!isMessageRecieved(Core.store.getState().StateSocial, payload.gift.id)) {
                    Core.store.dispatch(Actions.receiveMessage(payload.gift.id));

                    if (payload.type === PayloadType.SEND_GIFT) {
                        Core.store.dispatch(Actions.receiveFriendGift(payload.userId));
                    }

                    if (payload.gift.giftData) {
                        for (const gift of payload.gift.giftData) {
                            const idMap = {
                                Coin: ItemIDs.COINS,
                                coin: ItemIDs.COINS,
                                coins: ItemIDs.COINS,
                                shuffle: ItemIDs.SHUFFLE,
                                Life: ItemIDs.HEART,
                                life: ItemIDs.HEART,
                                Lives: ItemIDs.HEART,
                                lives: ItemIDs.HEART,
                                heart: ItemIDs.HEART,
                                hearts: ItemIDs.HEART,
                                Hearts: ItemIDs.HEART,
                                Flashlight: ItemIDs.HIGHLIGHT,
                                flashlight: ItemIDs.HIGHLIGHT,
                                highlight: ItemIDs.HIGHLIGHT,
                            };
                            const isInMap = Object.keys(idMap).includes(gift.itemId);
                            const isId = Object.values(ItemIDs).includes(gift.itemId);
                            if (!isInMap && !isId) {
                                if (sgPlatform === 'fbinstant') {
                                    FBInstant.logEvent('Unexpected_payload_gift', 1, {
                                        passedId: gift[0],
                                    });
                                }
                            } else {
                                // @ts-ignore
                                const giftId = isInMap ? idMap[gift.itemId] : gift.itemId;
                                Core.store.dispatch(
                                    Actions.receiveGift([giftId, gift.amount, payload.userId])
                                );
                            }
                        }
                    }
                }
            }
        }

        this.currentPayloads = [];

        this.xPromoPayloads.forEach(p => {
            sdkLogger.debug(['tracking all xPromo payloads', p]);
            EntryTracking.allXPromo(
                p.originGameId,
                p.promoReward,
                Object.keys(Core.store.getState().StatePlayer.mapLevels).length === 0 ||
                    Core.store.getState().StatePlayer.totalStars === 0
            );
        });

        this.xPromoPayloads = [];
    }

    /**
     * handles entry point for player entering game through an invitation
     * store in state to show rewards..
     * send confirmation message back OR...store in state if connection is not established
     */
    public async processReceivedInvites() {
        if (Core.store.getState().StatePlayer.totalStars > 0) {
            return;
        }

        sdkLogger.debug(['received invites: ', this.receivedInvites]);
        const filteredReceivedInvites = this.receivedInvites.filter(element => {
            return element.userId && element.userId !== Core.sdk.user.userId;
        });

        if (filteredReceivedInvites.length === 0) {
            return;
        }

        const received = [...filteredReceivedInvites];
        const previousRecievedIds = Core.store
            .getState()
            .StateSocial.receivedInvitations.map(i => i.fromId);
        sdkLogger.debug(['previous received invites: ', previousRecievedIds]);
        received.filter(i => !previousRecievedIds.includes(i.userId));
        sdkLogger.debug(['invites after filtering: ', received]);
        for (const invite of received) {
            sdkLogger.debug(['rinvite to confirm: ', invite]);
            await this.joinedCustomMessage(invite.userId)
                .then(() => {
                    Core.store.dispatch(Actions.storeReceivedInvites(invite.userId));
                })
                .catch(() => {
                    // store to add later
                });
            // failure or no connection....store received to dispatch later
        }

        this.receivedInvites = [];
    }

    /**
     * handles messages of confirmed friend invitation
     * used to show popup and receive rewards
     */
    public async processConfirmedInvites() {
        sdkLogger.debug(['Confirmed Invites: ', this.confirmedInvites]);
        if (this.confirmedInvites.length === 0) {
            return;
        }

        const friends = await this.getFriends();
        sdkLogger.debug(['Friends:  ', friends]);
        const invites = [...this.confirmedInvites];
        const previousInviteIds = Core.store
            .getState()
            .StateSocial.confirmedInvitations.map(s => s.userId);
        sdkLogger.debug(['Previous invites', previousInviteIds]);
        invites.filter(i => !previousInviteIds.includes(i.userId));
        sdkLogger.debug(['Previous Invites Ids filtered: ', invites]);
        for (const invite of invites) {
            if (!isMessageRecieved(Core.store.getState().StateSocial, invite.gift.id)) {
                Core.store.dispatch(Actions.receiveMessage(invite.gift.id));

                if (friends !== null && friends.length > 0) {
                    const friend = friends.filter((element: any) => {
                        return element.userId === invite.userId;
                    })[0];
                    sdkLogger.debug(['Filtered friend: ', friend]);
                    if (friend) {
                        sdkLogger.debug(['Filtered friend sent: ', friend]);
                        Core.store.dispatch(
                            Actions.storeConfirmedInvites([
                                friend.avatar,
                                friend.name,
                                friend.userId,
                            ])
                        );
                        // we don't need another action...it should be removed by pending with inviteConfirm
                        // any leftovers due to group messages and such should be cleared in another place...maybe when all processing complete
                        // Core.store.dispatch(Actions.removeInvitation());
                    }
                }
            }
        }
        this.confirmedInvites = [];
    }

    // for now just dealing with game end flow, replace with generic message
    public askFriend(friend: IFBFriend, item: IRewardItem): Promise<boolean> {
        return this.platform.askFriend(friend, item, this.getHelperGift());
    } // for now just dealing with game end flow, replace with generic message

    public getContextPlayers(): Promise<ContextPlayer[]> {
        return this.platform.getContextPlayers();
    }

    public getRandomFriend(): Promise<IFBFriend> {
        return this.platform.getRandomFriend();
    }

    public async sendCustomMessage(
        text: string,
        image: string,
        friendId?: string,
        payload?: IPayloadData,
        cta?: string
    ): Promise<any> {
        return await this.platform.sendCustomMessage(text, image, friendId, payload, cta);
    }

    public async share(shareMoment: string, sharePayload?: string): Promise<any> {
        let shareStuff = null;

        if (shareMoment === ShareableMoments.NEW_MAP) {
            shareStuff = await ShareHelpers.createMapCompleteSharePayload(sharePayload);
        } else if (shareMoment === ShareableMoments.JACKPOT) {
            shareStuff = await ShareHelpers.createJackpotSharePayload();
        }

        const deltaTrackingData = Core.delta.trackCurPropGetters.get(ParamGroups.SAVED)();
        if (shareStuff) {
            return await this.platform.share(shareStuff).then(() => {
                Core.delta.setSavedParams({
                    numSocialShares: deltaTrackingData.numSocialShares + 1,
                });
                Core.delta.addTrackedEvent(DDNAEvents.SOCIAL_ACTION, {
                    socialActionType: SocialActionTypes.SHARE,
                });
            });
        } else {
            return Promise.reject('no moment to share');
        }
    }

    public async playWithFriends(shareText: string): Promise<ContextPlayer[]> {
        return await this.platform.playWithFriends(shareText);
    }

    public async getFriends() {
        return await this.platform.getFriends();
    }

    /**
     * Send invite and open a context with a friend or a group of friends.
     */

    public async sendInvite(): Promise<boolean> {
        sdkLogger.debug('Trying to send an invite friend(s)...');
        try {
            const result = await this.playWithFriends('playwithfriends');
            const sent = await this.inviteCustomMessage();
            sdkLogger.debug(['Success playWithFriends:', result]);
            if (sent) {
                SocialTracking.switchInvite();
                Core.store.dispatch(Actions.addInvitation(result[0].getID()));
                Core.delta.addTrackedEvent(DDNAEvents.SOCIAL_ACTION, {
                    socialActionType: SocialActionTypes.INVITE,
                });
            }
            return true;
        } catch (error) {
            sdkLogger.debug(['Failed to send invite:', error]);
            return false;
        }
    }

    /**
     * Try to open a context with a friend and send coins via custom message.
     * @param friendId Friend id that the player will send a gift
     */
    public async sendCoins(friendId: string, coins: number): Promise<boolean> {
        const hasContext = await this.createContextAsync(friendId);
        if (!hasContext) {
            return false;
        } else {
            SocialTracking.switchCoins();
        }

        const base64 = await InviteBotImage.getSendCoinsBase64Async();

        try {
            Core.sdk.messages
                .sendCustomMessage(
                    'botSendCoinsMsg',
                    base64,
                    friendId,
                    {
                        // payload
                        type: PayloadType.SEND_COINS,
                        userId: Core.sdk.user.userId,
                        userName: Core.sdk.user.name,
                        rewardedUserId: friendId,
                        gift: this.getPayloadGiftData(GiftType.COINS, coins, getRandomId(12)), // TODO: amount
                    },
                    Core.local.getText('botSendCoinsBtn')
                )
                .then(success => {
                    Core.delta.addTrackedEvent(DDNAEvents.SOCIAL_ACTION, {
                        socialActionType: SocialActionTypes.SEND_COIN,
                        targetUserID: friendId,
                    });
                });

            return true;
        } catch (error) {
            console.log('Failed to send coins to a friend:', error);
        }

        return false;
    }

    /**
     * Try to open a context with a friend and send a gift via custom message.
     * @param friendId Friend id that the player will send a gift
     */
    public async sendGift(friendId: string, shouldCreateContext: boolean = true): Promise<boolean> {
        if (shouldCreateContext) {
            const hasContext = await this.createContextAsync(friendId);
            if (!hasContext) {
                return false;
            } else {
                SocialTracking.switchGiftSend();
            }
        }

        const base64 = await InviteBotImage.getSendGiftBase64Async();

        try {
            Core.sdk.messages.sendCustomMessage(
                'botSendGiftMsg',
                base64,
                friendId,
                {
                    // payload
                    type: PayloadType.SEND_GIFT,
                    userId: Core.sdk.user.userId,
                    userName: Core.sdk.user.name,
                    rewardedUserId: friendId,
                    gift: this.getRandomGift(),
                },
                Core.local.getText('botSendGiftBtn')
            );

            Core.delta.addTrackedEvent(DDNAEvents.SOCIAL_ACTION, {
                socialActionType: SocialActionTypes.SEND_GIFT,
                targetUserID: friendId,
            });
            return true;
        } catch (error) {
            console.log('Failed to send a gift to a friend:', error);
            return false;
        }
    }

    /**
     * Try to open a context with a friend and request a gift via custom message.
     * @param friendId Friend id that the player will request a gift
     */
    public async requestGift(friendId: string): Promise<boolean> {
        console.log('Trying to request a gift to a friend...');

        const hasContext = await this.createContextAsync(friendId);
        if (!hasContext) {
            return false;
        }
        SocialTracking.switchGiftRequest();

        const base64 = await InviteBotImage.getRequestGiftBase64Async();

        try {
            this.sendCustomMessage(
                'botRequestGiftMsg',
                base64,
                friendId,
                {
                    // payload
                    type: PayloadType.REQUEST_GIFT,
                    userId: Core.sdk.user.userId,
                    userName: Core.sdk.user.name,
                },
                Core.local.getText('botRequestGiftBtn')
            );
            Core.delta.addTrackedEvent(DDNAEvents.SOCIAL_ACTION, {
                socialActionType: SocialActionTypes.REQUEST_GIFT,
                targetUserID: friendId,
            });
            return true;
        } catch (error) {
            console.log('Failed to request a gift to a friend:', error);
            return false;
        }
    }

    /**
     * Send a custom message inviting friend(s)
     */
    private async inviteCustomMessage(): Promise<boolean> {
        sdkLogger.debug('Trying to send an invite custom message...');
        const base64 = await InviteBotImage.getInviteFriendBase64Async();

        try {
            await this.sendCustomMessage('invitefriend', base64, undefined, {
                // payload
                type: PayloadType.RECEIVED_INVITE,
                userId: Core.sdk.user.userId,
                userName: Core.sdk.user.name,
            });
            return true;
        } catch (error) {
            sdkLogger.debug(['Failed to send an invite message to a friend:', error]);
            return false;
        }
    }

    private async createContextAsync(friendId: string): Promise<boolean> {
        try {
            // Attempt to create a context with a friend.
            await this.platform.createContext(friendId);
            return true;
        } catch (error) {
            // Same context, player closed the dialog, ...
            const sameContext = error.code === 'SAME_CONTEXT';
            if (!sameContext) {
                sdkLogger.debug(`Failed to create a context with a friend:`);
            }
            return sameContext;
        }
    }

    /**
     * Send a custom message back to friend who invited the player.
     * @param friendId Id of the friend the player who invited
     */

    private async joinedCustomMessage(friendId: string): Promise<boolean> {
        sdkLogger.debug('Trying to send an joined custom message...');
        const base64 = await InviteBotImage.getFriendJoinedBase64Async();

        try {
            return await this.sendCustomMessage(
                'inviteconfirmed',
                base64,
                friendId,
                {
                    // payload
                    type: PayloadType.CONFIRMED_INVITE,
                    userId: Core.sdk.user.userId,
                    userName: Core.sdk.user.name,
                    rewardedUserId: friendId,
                    gift: this.getPayloadGiftData(GiftType.COINS, 500, this.getRewardGiftId()),
                },
                Core.local.getText('botSendGiftBtn')
            )
                .then((success: any) => {
                    sdkLogger.debug(['Success customMessage:', success]);
                    return true;
                })
                .catch((error: any) => {
                    sdkLogger.debug(['Failed customMessage:', error]);
                    return false;
                });
        } catch (error) {
            sdkLogger.debug(['Failed to send a join message to a friend:', error]);
        }

        return false;
    }

    /**
     * Generate a reward gift Id.
     */
    private getRewardGiftId(): string {
        return Core.sdk.user.userId + 'Joined';
    }

    /**
     * Return a gift data payload.
     */
    private getPayloadGiftData(type: GiftType, amount: number, id: string): IPayloadGiftData {
        return {
            giftData: [
                {
                    amount,
                    itemId: type,
                },
            ],
            id,
        };
    }

    /**
     * Return a random gift to send to a friend.
     */
    private getRandomGift(): IPayloadGiftData {
        const rewardItem: IRewardItem = getRandomReward(sendRequestRewardItems);
        // @ts-ignore
        const key = Object.keys(ItemIDs).find(x => ItemIDs[x] === rewardItem.id);
        // @ts-ignore
        const type = GiftType[key];
        return this.getPayloadGiftData(type, rewardItem.val as number, getRandomId(12));
    }

    private getHelperGift(): IPayloadGiftData {
        return {
            giftData: [
                {
                    amount: 25,
                    itemId: ItemIDs.COINS,
                },
            ],
            id: getRandomId(12),
        };
    }
}
